<template>
  <div class="customer-layout">
    <header class="customer-layout__header">
      <div class="customer-layout__header-inner">
        <div class="customer-layout__logo-section">
          <button
            v-if="isMobile"
            type="button"
            class="customer-layout__menu-btn"
            aria-label="Open menu"
            @click="sidebarDrawerVisible = true"
          >
            <i class="pi pi-bars"></i>
          </button>
          <router-link to="/" class="customer-layout__logo-link">
            <img src="@/assets/omind_logo.png" alt="OMIND" class="customer-layout__logo" />
          </router-link>
          <div class="customer-layout__header-divider"></div>
          <h1 class="customer-layout__title">
            {{ headerTitle }}
          </h1>
        </div>
        <div class="customer-layout__header-center">
          <UserProjectsContextSelect v-if="showUserProjectsContext" />
        </div>
        <div class="customer-layout__actions">
          <div v-if="devTipsEnabled" class="customer-layout__devtip">
            <DevTipIconComponent />
          </div>
          <!-- Level star: separate item between dev tip and profile (not a badge on profile) -->
          <div
            v-if="userLevel"
            class="customer-layout__level-badge-wrap"
            @mouseenter="levelTooltipVisible = true"
            @mouseleave="levelTooltipVisible = false"
          >
            <span class="customer-layout__level-badge">
              <font-awesome-icon icon="fa-star" class="text-5xl"/>
              <span class="customer-layout__level-num">{{ userLevel }}</span>
            </span>
            <!--Transition name="tooltip-fade">
              <div v-if="levelTooltipVisible" class="customer-layout__level-tooltip">
                {{ levelTooltipText }}
              </div>
            </Transition-->
          </div>
          <div class="customer-layout__profile-wrap">
            <button
              type="button"
              class="customer-layout__profile-btn"
              aria-haspopup="true"
              aria-controls="profile_menu"
              @click="toggleProfileMenu"
            >
              <img
                v-if="profilePicture"
                :src="profilePicture + '?_t=' + refreshValue"
                alt=""
                class="customer-layout__avatar"
              />
              <img v-else :src="defaultProfileImage" alt="" class="customer-layout__avatar" />
            </button>
            <Menu
              ref="profileMenuRef"
              id="profile_menu"
              :model="profileMenuItems"
              :popup="true"
              append-to="body"
              class="customer-layout__profile-menu"
            />
          </div>
        </div>
      </div>
    </header>

    <div class="customer-layout__main">
      <template v-if="!isMobile">
        <CustomerSidebar />
      </template>
      <Drawer
        v-model:visible="sidebarDrawerVisible"
        position="left"
        :style="{ width: '260px' }"
        class="customer-layout__drawer"
        @hide="sidebarDrawerVisible = false"
      >
        <template #header>
          <span class="customer-layout__drawer-title">{{ headerTitle }}</span>
        </template>
        <CustomerSidebar />
      </Drawer>
      <main class="customer-layout__content">
        <div class="customer-layout__page">
          <router-view />
        </div>
      </main>
    </div>

    <footer class="customer-layout__footer">
      <span>
        © Copyright 2026 |
        <a
          href="https://omind.de/"
          target="_blank"
          rel="noopener noreferrer"
          class="customer-layout__footer-link"
        >
          OMIND platform GmbH
        </a>
      </span>
    </footer>

  </div>

  <PushNotificationComponent />
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Menu from 'primevue/menu';
import Drawer from 'primevue/drawer';
import DevTipIconComponent from '@/components/Home/Shared/DevTip/DevTipComponent.vue';
import CustomerSidebar from './CustomerSidebar.vue';
import UserProjectsContextSelect from '@/components/CustomerApp/UserProjectsContextSelect.vue';
import PushNotificationComponent from '@/components/Home/Shared/PushNotifications/PushNotificationComponent.vue';
import defaultProfileImage from '@/assets/defaultProfileImage.js';
import NetworkAuth from '@/helpers/networking/NetworkAuth';
import {
  getCatalogKeyForRoute,
  getFeatureDisplayNameOrFallback,
  normalizeFeatureLocale,
} from '@/utils/featureDisplay';
import { isFeatureVisibleForCustomer } from '@/utils/featureAccess';
import {
  TENANT_ROLE_ADMIN,
  TENANT_ROLE_COMPANY_SUPERUSER,
  TENANT_ROLE_TEAM_LEADER,
} from '@/services/enumService';

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t, locale } = useI18n();

const profileMenuRef = ref(null);
const sidebarDrawerVisible = ref(false);
const isMobile = ref(false);
const levelTooltipVisible = ref(false);
let tokenRefresher = null;
let resizeListener = null;

const devTipsEnabled = computed(() => isFeatureVisibleForCustomer(store, 'developmentTip'));

const showUserProjectsContext = computed(() => route.path.startsWith('/user-projects'));

const headerTitle = computed(() => {
  const titleKey = route.meta?.titleKey || 'CustomerSidebar.home';
  const fallback = t(titleKey);
  const catalogKey = route.meta?.catalogKey || getCatalogKeyForRoute(route.path);
  if (!catalogKey) return fallback;
  return getFeatureDisplayNameOrFallback(
    store,
    catalogKey,
    fallback,
    normalizeFeatureLocale(locale.value),
  );
});

const user = computed(() => store.getters.getUser);
const userLevel = computed(() => {
  return store.getters.getUserLevel ?? '';
});

const levelTooltipText = computed(() => {
  const raw = store.getters.getUserLevel ?? 0;
  const parsed = Number.parseInt(String(raw), 10);
  const current = Number.isFinite(parsed) ? parsed : 0;
  const levelCompleted = store.getters.getLevelCompleted;
  const overview = store.getters.getLevelProgressOverview;
  const totalLevels = overview?.totalLevels;

  const onHighestConfiguredLevel =
    typeof totalLevels === 'number' &&
    totalLevels > 0 &&
    current > 0 &&
    current >= totalLevels;

  if (onHighestConfiguredLevel) {
    if (levelCompleted) {
      return t('HeaderComponent.highestLevelCompletedTooltip', { level: String(current) });
    }
    return t('HeaderComponent.highestLevelGoalsTooltip', { level: String(current) });
  }

  if (levelCompleted) {
    return t('HeaderComponent.maxLevelTooltip', { level: String(current + 1) });
  }
  return t('HeaderComponent.currentLevelTooltip', {
    current: String(current),
    next: String(current + 1),
  });
});
const userRoles = computed(() => store.getters.getUserRoles || []);
const permissions = computed(() => ({
  admin: userRoles.value.includes(TENANT_ROLE_ADMIN),
  companySuperuser: userRoles.value.includes(TENANT_ROLE_COMPANY_SUPERUSER),
  teamLeader: userRoles.value.includes(TENANT_ROLE_TEAM_LEADER),
}));
const refreshValue = computed(() => store.getters.getRefreshValue ?? 0);

const profilePicture = computed(() => {
  const pic = user.value?.picture_profile?.small;
  return pic || null;
});

const profileMenuItems = computed(() => [
  {
    label: user.value?.name ?? '',
    items: [
      {
        label: t('HeaderComponent.goToProfile'),
        icon: 'pi pi-user-edit',
        command: () => router.push('/edit-profile'),
      },
      {
        label: t('HeaderComponent.logout'),
        icon: 'pi pi-sign-out',
        command: () => {
          store.dispatch('logout');
          router.push('/auth');
        },
      },
    ],
  },
]);

function toggleProfileMenu(event) {
  profileMenuRef.value?.toggle(event);
}

watch(() => route.path, () => {
  sidebarDrawerVisible.value = false;
});

function checkMobile() {
  isMobile.value = typeof window !== 'undefined' && window.innerWidth < 1024;
}

onMounted(async () => {
  checkMobile();
  resizeListener = () => checkMobile();
  window.addEventListener('resize', resizeListener);
  if (import.meta.env.VITE_KEYCLOAK === 'false') {
    tokenRefresher = setInterval(async () => {
      const token = store.state.auth?.token;
      if (NetworkAuth.isTokenExpired(token, 2000)) {
        await store.dispatch('refreshToken');
      }
    }, 1000);
  }
  store.dispatch('getNotifications');
  if (!user.value?.userId) {
    await store.dispatch('getUserInfo');
  }
  const currentLevel = store.getters.getUserLevel;
  if (currentLevel === '' || currentLevel === null || currentLevel === undefined) {
    try {
      await store.dispatch('getCurrentLevelInfo');
    } catch (err) {
      console.error('Error loading level information:', err);
    }
  }
  if (user.value?.userId) {
    store.dispatch('fetchLevelProgressOverview').catch(() => {});
  }
});

onUnmounted(() => {
  if (tokenRefresher) clearInterval(tokenRefresher);
  if (resizeListener) window.removeEventListener('resize', resizeListener);
});
</script>

<style scoped>
.customer-layout {
  --layout-bg: #f8f8f8;
  --layout-header-bg: #ffffff;
  --layout-border: #e7e7e7;
  --layout-text: #1E2019;
  --layout-accent-bg: rgba(228, 130, 26, 0.1);
  --layout-accent-border: rgba(228, 130, 26, 0.2);
  --layout-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  --layout-footer-bg: #1E2019;
  --layout-footer-text: #ffffff;

  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--layout-bg);
  font-family: 'AsenPro', sans-serif;
}

.customer-layout__header {
  flex-shrink: 0;
  --header-height: 56px;
  height: var(--header-height);
  background: var(--layout-header-bg);
  border-bottom: 1px solid var(--layout-border);
  box-shadow: var(--layout-shadow);
  z-index: 100;
}

@media (min-width: 640px) {
  .customer-layout__header {
    --header-height: 64px;
  }
}

.customer-layout__main {
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: hidden;
}

.customer-layout__header-inner {
  height: 100%;
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0 1rem 0 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  position: relative;
}

.customer-layout__header-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  min-width: 0;
  width: min(calc(100% - 24rem), 20rem);
  display: flex;
  justify-content: center;
  pointer-events: none;
  z-index: 1;
}

.customer-layout__header-center > * {
  pointer-events: auto;
  width: 100%;
}

.customer-layout__logo-section {
  flex: 0 1 auto;
  min-width: 0;
  margin-right: auto;
}

.customer-layout__actions {
  flex: 0 0 auto;
  margin-left: auto;
}

@media (min-width: 640px) {
  .customer-layout__header-inner {
    padding: 0 1.5rem 0 1rem;
  }
}

.customer-layout__logo-section {
  height: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.customer-layout__menu-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: var(--layout-accent-bg);
  border-radius: 0.5rem;
  color: var(--color-orange, #e4821a);
  font-size: 1.25rem;
  cursor: pointer;
}

.customer-layout__menu-btn:hover {
  background: var(--layout-accent-border);
}

.customer-layout__drawer-title {
  font-weight: 600;
  color: var(--layout-text);
}

.customer-layout__drawer :deep(.p-drawer-content) {
  padding: 0;
}

.customer-layout__logo-link {
  display: flex;
  align-items: center;
}

.customer-layout__logo {
  width: auto;
  max-height: 72px;
}

@media (min-width: 640px) {
  .customer-layout__logo {
    max-height: 72px;
  }
}

.customer-layout__header-divider {
  width: 1px;
  height: 100%;
  min-height: 32px;
  background: linear-gradient(180deg, transparent, rgba(228, 130, 26, 0.3), transparent);
}

@media (max-width: 768px) {
  .customer-layout__title,
  .customer-layout__header-divider {
    display: none !important;
  }
}

.customer-layout__title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--layout-text);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
}

.customer-layout__title i {
  color: var(--color-orange, #e4821a);
}

.customer-layout__actions {
  height: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  overflow: visible;
}

.customer-layout__devtip {
  width: calc(var(--header-height) * 0.75);
  height: calc(var(--header-height) * 0.75);
  min-width: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.customer-layout__level-badge {
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: var(--color-orange, #e4821a);
}

.customer-layout__level-tooltip {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 6px;
  padding: 6px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--layout-text);
  background: #fff;
  border: 1px solid var(--layout-border);
  border-radius: 6px;
  box-shadow: var(--layout-shadow);
  white-space: nowrap;
  z-index: 200;
  pointer-events: none;
}

.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.15s ease;
}
.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
}

.customer-layout__level-num {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1rem;
  font-weight: 700;
  color: white;
}

.customer-layout__profile-wrap {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.customer-layout__profile-btn {
  width: calc(var(--header-height) * 0.90);
  height: calc(var(--header-height) * 0.90);
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.customer-layout__avatar {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.customer-layout__content {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--layout-bg);
}

.customer-layout__page {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  width: 75vw;
  max-width: 75vw;
  margin: 0 auto;
  padding: 1rem 1.5rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

/* --- Viewport-unit overrides ---
   Page components were built for a full-viewport layout (no sidebar).
   Override vw/vh units so they fill the content area instead. */

.customer-layout__page :deep(.w-screen) {
  width: 100% !important;
}

.customer-layout__page :deep([style*="width: 100vw"]) {
  width: 100% !important;
}

.customer-layout__page :deep(.min-h-screen) {
  min-height: auto !important;
}

/* Override large viewport-aware margins */
@media (min-width: 1024px) {
  .customer-layout__page :deep(.lg\:ml-12) {
    margin-left: 1rem !important;
  }
}
@media (min-width: 1280px) {
  .customer-layout__page :deep(.xl\:ml-20) {
    margin-left: 1rem !important;
  }
}
@media (min-width: 1536px) {
  .customer-layout__page :deep(.2xl\:ml-36) {
    margin-left: 1rem !important;
  }
}

@media (max-width: 768px) {
  .customer-layout__page {
    width: 100vw;
    max-width: 100vw;
    padding: 0.25rem;
  }
}

@media (max-width: 1024px) {
  .customer-layout__page {
    width: 95vw;
    max-width: 95vw;
  }
}

.customer-layout__footer {
  flex-shrink: 0;
  width: 100%;
  padding: 0.5rem 1.5rem;
  background: var(--layout-footer-bg);
  color: var(--layout-footer-text);
  font-size: 0.8125rem;
  text-align: right;
}

.customer-layout__footer-link {
  color: var(--layout-footer-text);
  text-decoration: none;
  font-weight: normal;
}

.customer-layout__footer-link:hover {
  color: var(--color-orange, #e4821a);
}
</style>

<!-- Unscoped overrides for child component scoped styles that use viewport units.
     These components use scoped `min-height: 100vh` and `width: 100vw` on .wrapper,
     which is wrong inside a sidebar layout. -->
<style>
.customer-layout .customer-layout__page .wrapper {
  min-height: auto !important;
  width: 100% !important;
}

.customer-layout__profile-menu ul,
.customer-layout__profile-menu ol {
  list-style: none !important;
  list-style-type: none !important;
  padding-left: 0 !important;
  margin-left: 0 !important;
}

.customer-layout__profile-menu li {
  list-style: none !important;
  list-style-type: none !important;
  padding-left: 0 !important;
  margin-left: 0 !important;
}

/* User name (submenu label) in profile dropdown */
.customer-layout__profile-menu .p-submenu-header,
.customer-layout__profile-menu .p-submenu-label {
  margin-left: 0.75rem !important;
}
</style>
